import React from "react";
import { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import Calendar from "../Calendar";
import DelayInput from "../DelayInput";

import { V2 } from "../../Api";
import { TZ } from "../../Utilities";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../viewer.css";

interface ISurgeryCalendarViewState {
    month: string;

    locations: any[];
    searchLocation: string;
    searchStatus: string;
    searchType: string;

    displayBefore: DateTime;
    displayAfter: DateTime;

    events: { [date: number]: Array<{ title: string }>; };
    isLoading: boolean | string;

    [name: string]: any;
}

class SurgeryCalendarView extends React.Component<RouteComponentPropsHistory<any, any>, ISurgeryCalendarViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
        super(props);

        const today = DateTime.local();

        let month = today.toFormat("yyyy-MM");
        let searchLocation = "";
        let searchStatus = "";
        let searchType = "";

        if (props.location.state) {
            // Show last shown month
            month = props.location.state.month || month;
            searchLocation = props.location.state.searchLocation;
            searchStatus = props.location.state.searchStatus;
            searchType = props.location.state.searchType;
        }

        this.state = {
            month,

            events: [],
            locations: [],

            searchLocation,
            searchStatus,
            searchType,

            displayBefore: today.endOf("month"),
            displayAfter: today.startOf("month"),

            isLoading: true,
        };

        this.viewDayDetails = this.viewDayDetails.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.displayRangeChanged = this.displayRangeChanged.bind(this);
    }

    public async componentDidMount() {
        try {
            const locations = await V2.get<any[]>("locations?type=surgery");

            this.setState({
                locations,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }

    }

    public render() {
        return (
            <div className="viewer-root viewer-full full-height">
                <div className="filter-control-box">
                    <DelayInput timeout={500} type="month" className="fill-space" name="month" value={this.state.month} onChangeDelayed={this.handleElementChange} />
                    <select value={this.state.searchType} name="searchType" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All types</option>
                        <option value="!office">All but office</option>
                        <option value="normal">OR</option>
                        <option value="office">Office</option>
                        <option value="officeindigent">Indigent</option>
                    </select>
                    <select value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="complete">Completed</option>
                        <option value="noshow">Soft-canceled</option>
                        <option value="canceled">Canceled</option>
                        <option value="notneeded">Canceled - Not Needed</option>
                        <option value="failed">Failed</option>
                    </select>
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                </div>
                <div className="spinner-overlay-root full-height">
                    <Calendar className="full-height" year={parseInt(this.state.month.substr(0, 4), 10)} month={parseInt(this.state.month.substr(5, 2), 10)} highlightDate={new Date()} events={this.state.events} onDaySelected={this.viewDayDetails} onDisplayRangeChanged={this.displayRangeChanged} />
                    {
                        this.state.isLoading ? (
                            <div className="spinner-overlay">
                                {
                                    (typeof this.state.isLoading === "string") ?
                                        <span className="error-text-big">{this.state.isLoading}</span> :
                                        <div className="lds-dual-ring-big" />
                                }
                            </div>
                        ) : ""
                    }
                </div>
            </div>
        );
    }

    private async loadAppointments() {
        try {
            this.setState({
                events: {},
                isLoading: true,
            });

            // Only request the visible range's events
            const beginDate = this.state.displayAfter;
            const endDate = this.state.displayBefore.plus({ millisecond: 1 });

            const parameters = [
                `tz=${encodeURIComponent(TZ())}`,
                `after=${encodeURIComponent(beginDate.toISO())}`,
                `before=${encodeURIComponent(endDate.toISO())}`,
                "group=day,location",
                "group2=complete"
            ];
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }
            if (this.state.searchType) {
                parameters.push(`type=${this.state.searchType}`);
            }

            const appts = await V2.get<any>(`surgery/count?${parameters.join("&")}`);

            const bins: { [date: number]: Array<{ title: string, count: number, type?: string }>; } = {};

            const monthBase = DateTime.fromISO(this.state.month);
            for (const group of appts.groups) {
                const date = DateTime.fromISO(group.date).diff(monthBase, "days").days + 1;
                if (!bins[date]) {
                    bins[date] = [];
                }

                bins[date].push({
                    count: group.count,
                    title: !group.completeCount ? `${group.count} ${group.shortName || group.name}` : `${group.completeCount} / ${group.count} ${group.shortName || group.name}`
                });
            }

            this.setState({
                events: bins,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                events: {},
                isLoading: "Could not load calendar"
            });
        }
    }

    private viewDayDetails(date: Date) {
        this.saveSearchState();

        this.props.history.push(`/surgery/calendar/${date.toISOString().substr(0, 10)}`, { searchLocation: this.state.searchLocation, searchStatus: this.state.searchStatus, searchType: this.state.searchType });
    }

    private saveSearchState() {
        // Stash the currently displayed month so the user can come back to it
        const currentLocation = this.props.location;
        currentLocation.state = {
            month: this.state.month,

            searchLocation: this.state.searchLocation,
            searchStatus: this.state.searchStatus,
            searchType: this.state.searchType,
        };
        this.props.history.replace(currentLocation);
    }

    private handleElementChange(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        if (name === "month" && !value) {
            return;
        }

        this.setState({
            [name]: value
        }, () => {
            this.saveSearchState();
            if (name !== "month") {
                this.loadAppointments();
            }
        });
    }

    private displayRangeChanged(minDate: Date, maxDate: Date) {
        this.setState({
            displayBefore: DateTime.fromJSDate(maxDate).toLocal(),
            displayAfter: DateTime.fromJSDate(minDate).toLocal()
        }, () => this.loadAppointments());
    }
}

export default withAuth(withTitle("Surgery Calendar", SurgeryCalendarView));
